// DevHub Home Page — Search-centric dashboard with action card grid + About section
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { HomeSearch } from './HomeSearch';
import { HomePersonalized } from './HomePersonalized';
import { homeMessages } from '../../i18n/home';
import { useLink } from '../common';
import './HomePage.css';

export const HomePage = () => {
  const link = useLink();
  const { t } = useTranslationRef(homeMessages);

  return (
    <div className="devhub-home">
      {/* Search hero — the focal point */}
      <section className="devhub-home-search-hero">
        <h1 className="devhub-home-search-hero__title">{t('hero.title')}</h1>
        <HomeSearch />
        <p className="devhub-home-search-hero__sub">{t('hero.sub')}</p>
        <div className="devhub-home-hero-ctas">
          <odx-button variant="primary" {...link('/docs/default/component/devhub-guides')}>
            {t('hero.gettingStarted')}
            <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
          <odx-button variant="secondary" {...link('/self-service/create')}>
            {t('hero.selfService')}
            <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </div>
      </section>

      {/* DELICATE personalization — renders null (nothing) for brand-new users
          with no recents and no stars, so Home looks exactly as before for them.
          Additive only; never reorders or restyles the hero/cards. */}
      <HomePersonalized />

      {/* Action card grid — primary entry points (mirrors the DevHub IA) */}
      <section className="devhub-home-cards">
        <article className="devhub-home-card">
          <odx-icon className="devhub-home-card__icon" name="core::training" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.guides.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.guides.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/docs/default/component/devhub-guides')}>
            {t('cards.guides.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>

        <article className="devhub-home-card">
          <odx-icon className="devhub-home-card__icon" name="core::tile-menu" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.catalog.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.catalog.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/catalog-overview')}>
            {t('cards.catalog.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>

        <article className="devhub-home-card">
          <odx-icon className="devhub-home-card__icon" name="core::document" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.docs.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.docs.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/docs-overview')}>
            {t('cards.docs.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>

        <article className="devhub-home-card">
          <odx-icon className="devhub-home-card__icon" name="core::plus" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.selfService.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.selfService.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/self-service/create')}>
            {t('cards.selfService.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>

        <article className="devhub-home-card">
          <odx-icon className="devhub-home-card__icon" name="core::toolbox" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.resources.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.resources.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/resources')}>
            {t('cards.resources.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>

        <article className="devhub-home-card is-star">
          <span className="devhub-home-card__pill">{t('cards.genai.pill')}</span>
          <odx-icon className="devhub-home-card__icon" name="core::ai-spark" size="md" />
          <h2 className="devhub-home-card__title">{t('cards.genai.title')}</h2>
          <p className="devhub-home-card__desc">{t('cards.genai.desc')}</p>
          <odx-button variant="secondary" size="sm" {...link('/docs/default/component/genai-at-draeger')}>
            {t('cards.genai.cta')} <odx-icon name="core::chevron-right" size="sm" slot="suffix" />
          </odx-button>
        </article>
      </section>

      {/* About section — 3 x 2 grid of capabilities.
          TEMPORARILY HIDDEN (awaiting finalized content). Restore by un-commenting
          this block; i18n strings still live in i18n/home.ts under `about.*`. */}
      {/*
      <section className="devhub-home-about">
        <h2 className="devhub-home-about__heading">{t('about.heading')}</h2>

        <div className="devhub-home-about__grid">
          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::menu" size="sm" />
            </span>
            <h3>{t('about.catalog.title')}</h3>
            <p>{t('about.catalog.desc')}</p>
          </div>

          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::document" size="sm" />
            </span>
            <h3>{t('about.docs.title')}</h3>
            <p>{t('about.docs.desc')}</p>
          </div>

          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::tile-menu" size="sm" />
            </span>
            <h3>{t('about.landscape.title')}</h3>
            <p>{t('about.landscape.desc')}</p>
          </div>

          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::plus" size="sm" />
            </span>
            <h3>{t('about.scaffolder.title')}</h3>
            <p>{t('about.scaffolder.desc')}</p>
          </div>

          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::link" size="sm" />
            </span>
            <h3>{t('about.api.title')}</h3>
            <p>{t('about.api.desc')}</p>
          </div>

          <div className="devhub-home-about__item">
            <span className="devhub-home-about__badge">
              <odx-icon name="core::search" size="sm" />
            </span>
            <h3>{t('about.search.title')}</h3>
            <p>{t('about.search.desc')}</p>
          </div>
        </div>
      </section>
      */}
    </div>
  );
};
